import { Validators } from './validators';

export class RelationsValidators extends Validators {
    public static readonly ERROR_TYPES = {
        scheduledOrders: 'Flag onlineOrders.scheduledOrders should be enabled for provided collectionType',
        landingPageLocationMode: 'Invalid location mode config. Venue or location config should be defined for landingPage element depending on app mode',
        locationListPageLocationMode: 'Invalid location mode config. Venue or location config should be defined for locationListPage element depending on app mode',
        orderConfirmationPageLiveViewSection: 'liveViewSection property should contain at least one element when isLiveViewEnabled flag is true',
        dependantPropertiesHasValues: 'Both properties have values. Only one should have a value.',
        dependantPropertiesNeitherValues: 'Neither property has a value. One should have a value.',
        dependantPropertiesNotExist: 'Properties not exist on checked object path.',
    };

    public static validateScheduledOrdersFlag(config: OLO.Config, ...mandatoryCollectionTypeIds: OLO.Enums.COLLECTION_TYPE[]): typeof RelationsValidators._errors {
        Validators._registerErrorTypes(RelationsValidators.ERROR_TYPES);

        RelationsValidators._errors = [];

        const isScheduledOrderingEnabled = config.onlineOrders.scheduledOrders === true;
        if (isScheduledOrderingEnabled) {
            return [];
        }

        const enabledCollectionTypes = config.collectionTypes.filter((obj) => obj.enabled === true && mandatoryCollectionTypeIds.includes(obj.collectionTypeId));
        if (enabledCollectionTypes.length) {
            enabledCollectionTypes.forEach((obj) => {
                RelationsValidators._errors.push({
                    scheduledOrders: {
                        path: 'collectionTypes',
                        foundIn: `${obj.collectionTypeId}`,
                        value: isScheduledOrderingEnabled,
                        context: null,
                    },
                });
            });
        }

        return RelationsValidators._errors;
    }

    public static validateLandingPageItemsLocationModes(config: OLO.Config): typeof RelationsValidators._errors {
        Validators._registerErrorTypes(RelationsValidators.ERROR_TYPES);

        RelationsValidators._errors = [];

        const isVenueMode = config.venue?.id != null && config.venue?.name != null;
        config.landingPage.forEach((obj, index) => {
            if (isVenueMode && obj.venueMode == null) {
                RelationsValidators._errors.push({
                    landingPageLocationMode: {
                        path: 'landingPage',
                        foundIn: `[${index}].venueMode`,
                        value: obj.venueMode,
                        context: null,
                    },
                });
            }

            if (!isVenueMode && obj.locationMode == null) {
                RelationsValidators._errors.push({
                    landingPageLocationMode: {
                        path: 'landingPage',
                        foundIn: `[${index}].locationMode`,
                        value: obj.locationMode,
                        context: null,
                    },
                });
            }
        });

        return RelationsValidators._errors;
    }

    public static validateLocationListPageItemsLocationModes(config: OLO.Config): typeof RelationsValidators._errors {
        Validators._registerErrorTypes(RelationsValidators.ERROR_TYPES);

        RelationsValidators._errors = [];

        const isVenueMode = config.venue?.id != null && config.venue?.name != null;
        config.locationListPage.forEach((obj, index) => {
            if (isVenueMode && obj.venueMode == null) {
                RelationsValidators._errors.push({
                    locationListPageLocationMode: {
                        path: 'locationListPage',
                        foundIn: `[${index}].venueMode`,
                        value: obj.venueMode,
                        context: null,
                    },
                });
            }

            if (!isVenueMode && obj.locationMode == null) {
                RelationsValidators._errors.push({
                    locationListPageLocationMode: {
                        path: 'locationListPage',
                        foundIn: `[${index}].locationMode`,
                        value: obj.locationMode,
                        context: null,
                    },
                });
            }
        });

        return RelationsValidators._errors;
    }

    public static validateOrderConfirmationPageLiveViewSection(config: OLO.Config): typeof RelationsValidators._errors {
        Validators._registerErrorTypes(RelationsValidators.ERROR_TYPES);

        RelationsValidators._errors = [];

        config.orderConfirmationPage.forEach((obj, index) => {
            if (obj.orderStatusSection.isLiveViewEnabled === true && !obj.orderStatusSection.liveViewSection.length) {
                RelationsValidators._errors.push({
                    orderConfirmationPageLiveViewSection: {
                        path: 'orderConfirmationPage',
                        foundIn: `[${index}].orderStatusSection.liveViewSection`,
                        value: obj.orderStatusSection.liveViewSection,
                        context: null,
                    },
                });
            }
        });

        return RelationsValidators._errors;
    }

    public static validateDependantProperties(config: OLO.Config, configPath: string, propertyA: string, propertyB: string): typeof RelationsValidators._errors {
        Validators._registerErrorTypes(RelationsValidators.ERROR_TYPES);

        RelationsValidators._errors = [];

        const dependentValueCheck = <T>(obj: T, propA: string, propB: string): void => {
            const propAValue = obj[propA];
            const propBValue = obj[propB];

            if (propAValue === undefined || propBValue === undefined) {
                RelationsValidators._errors.push({
                    dependantPropertiesNotExist: {
                        path: configPath,
                        foundIn: `${propA} and ${propB}`,
                        value: `${propA}: ${obj[propA]}, ${propB}: ${obj[propB]}`,
                        context: null,
                    },
                });

                return;
            }

            const hasProperty1Value = propAValue != null;
            const hasProperty2Value = propBValue != null;

            if (hasProperty1Value && hasProperty2Value) {
                RelationsValidators._errors.push({
                    dependantPropertiesHasValues: {
                        path: configPath,
                        foundIn: `${propA} and ${propB}`,
                        value: `${propA}: ${obj[propA]}, ${propB}: ${obj[propB]}`,
                        context: null,
                    },
                });
            }

            if (!hasProperty1Value && !hasProperty2Value) {
                RelationsValidators._errors.push({
                    dependantPropertiesNeitherValues: {
                        path: configPath,
                        foundIn: `${propA} and ${propB}`,
                        value: `${propA}: ${obj[propA]}, ${propB}: ${obj[propB]}`,
                        context: null,
                    },
                });
            }
        };

        const processPath = (obj: any, segments: string[]): void => {
            let current = obj;
            for (let i = 0; i < segments.length; i++) {
                const segment = segments[i];
                if (current === undefined || current === null || !(segment in current)) {
                    console.error(`Relations validator: Property ${segment} from ${configPath} does not exist in the object.`);

                    return;
                }
                current = current[segment];

                // If element from path is array then process it again
                if (Array.isArray(current)) {
                    for (const elCurrent of current) {
                        if (i < segments.length - 1) {
                            processPath(elCurrent, segments.slice(i + 1));
                        } else if (typeof elCurrent === 'object' && elCurrent !== null) {
                            dependentValueCheck(elCurrent, propertyA, propertyB);
                        } else {
                            console.error(`Relations validator: Element in array at ${configPath} is not an object.`);
                        }
                    }

                    return;
                }
            }

            if (typeof current === 'object' && current !== null) {
                dependentValueCheck(current, propertyA, propertyB);
            } else {
                console.error(`Relations validator: Property ${configPath} is neither an object nor an array.`);
            }
        };

        processPath(config, configPath.split('.'));

        return RelationsValidators._errors;
    }
}
